import type { DimensionScore } from '../api-client/api-schemas.ts'
import { pluralize } from '../text-utils.ts'
import { BAND_LABELS, formatPosturePercent, toPostureBand } from './scorecard-bands.ts'
import { meanScore } from './scorecard-score.ts'

/**
 * A plain-language reading of a repository's scorecard posture.
 *
 * **Named for posture, not risk.** Everything here is on the scorecard's 0–1 scale where higher is
 * better, and it speaks in that scale's vocabulary (`toPostureBand`, `BAND_LABELS`). The repositories
 * page's `riskScoreSummary` is a different thing on a different scale — 0–100 where higher is worse — and
 * the two used to share this name, which invited exactly the conversion `repository-risk.ts` refuses to
 * imply when it leaves its own bands unnamed.
 *
 * **Everything here is derived from the scores themselves** — the band, the counts, and the weakest
 * dimension by name. Nothing is asserted that the numbers below do not already say, because this sits
 * above the very cards a reader will check it against, and a summary that overreaches is worse than none.
 *
 * The weak dimensions get named rather than merely counted: "three score weak" sends you hunting, and the
 * whole point of a summary is to say where to look.
 */
export const scorecardPostureSummary = (dimensions: DimensionScore[]): string => {
  const scorable = dimensions.filter(dimension => Number.isFinite(dimension.score))
  const unscoredCount = dimensions.length - scorable.length

  if (scorable.length === 0) {
    return `None of the ${dimensions.length} ${pluralize(dimensions.length, 'dimension', 'dimensions')} below could be scored, so there is no cumulative posture score for this repository.`
  }

  const mean = meanScore(scorable.map(dimension => dimension.score)) ?? 0
  const band = toPostureBand(mean)
  const ranked = [...scorable].sort((a, b) => a.score - b.score)
  const dragging = ranked.filter(dimension => {
    const dimensionBand = toPostureBand(dimension.score)
    return dimensionBand === 'fair' || dimensionBand === 'weak'
  })

  // "the N dimensions below average X%" reads as "below average". Say "average" once, as the verb.
  const headline = `${BAND_LABELS[band]} overall — ${scorable.length} scored ${pluralize(scorable.length, 'dimension averages', 'dimensions average')} ${formatPosturePercent(mean)}.`

  const named = dragging
    .slice(0, 3)
    .map(dimension => `${dimension.displayName} (${formatPosturePercent(dimension.score)})`)
    .join(', ')
  const unnamedCount = dragging.length - Math.min(dragging.length, 3)
  const detail =
    dragging.length === 0
      ? 'No dimension falls below adequate, so no single area is holding the posture back.'
      : `${dragging.length} of ${scorable.length} ${pluralize(dragging.length, 'is', 'are')} below adequate: ${named}${unnamedCount > 0 ? `, and ${unnamedCount} more` : ''}.`

  const caveat =
    unscoredCount === 0
      ? undefined
      : `${unscoredCount} ${pluralize(unscoredCount, 'dimension', 'dimensions')} could not be scored and ${pluralize(unscoredCount, 'is', 'are')} excluded from the average.`

  return [headline, detail, caveat].filter(Boolean).join(' ')
}
